import React, {useEffect, useState,useRef} from "react";
import PropTypes from "prop-types";
import clsx from "clsx";

const Conversation = ({
    conversation = "",
    isHold = false,
    assistantReply = "",
    isLiveConversation = false,
    isAwaitingAssistantReply = false
}) => {
    const prompt = "Say \"Reco\" and tell me what you're looking for!";

    const userText = typeof conversation === "string" ? conversation.trim() : "";
    const replyText = typeof assistantReply === "string" ? assistantReply.trim() : "";
    const [lastUserText, setLastUserText] = useState(userText || prompt);
    const [lastAssistantReply, setLastAssistantReply] = useState(replyText);

    useEffect(() => {
        if (userText) {
            setLastUserText(userText);
            return;
        }
        if (!isLiveConversation) {
            setLastUserText(prompt);
        }
    }, [userText, isLiveConversation]);

    useEffect(() => {
        if (replyText) {
            setLastAssistantReply(replyText);
        }
    }, [replyText]);

    const displayReplyText = replyText || (isAwaitingAssistantReply ? lastAssistantReply : "");
    const showAssistantPulse = Boolean(userText) && (isHold || isAwaitingAssistantReply || (isLiveConversation && !replyText));
    const showAssistantRow = Boolean(userText) && (showAssistantPulse || Boolean(displayReplyText));

    const limitedReplyText =
        displayReplyText?.length > 90
            ? displayReplyText.slice(0, 90) + "..."
            : displayReplyText;

    const pulseDots = (
        <span className="dot-pulse" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
        </span>
    );

    const MarqueeText = ({ text }) => {
        const containerRef = useRef(null);
        const textRef = useRef(null);

        const [translateX, setTranslateX] = useState(0);
        const [duration, setDuration] = useState(0);
        const [opacity, setOpacity] = useState(1);

        const phaseRef = useRef("idle");
        const timeoutRef = useRef(null);

        const DELAY = 2000; // ⏳ 2s before every cycle
        const PAUSE = 2000;
        const FADE = 500;
        const SPEED = 30;

        const startSlide = () => {
            const container = containerRef.current;
            const textEl = textRef.current;

            if (!container || !textEl) return;

            const overflow = textEl.scrollWidth - container.offsetWidth;

            if (overflow > 0) {
                phaseRef.current = "sliding";

                setOpacity(1);
                setDuration(overflow / SPEED);
                setTranslateX(-overflow);
            }
        };

        const startCycle = () => {
            timeoutRef.current = setTimeout(() => {
                startSlide();
            }, DELAY);
        };

        useEffect(() => {
            const container = containerRef.current;
            const textEl = textRef.current;

            if (!container || !textEl) return;

            // reset state
            setTranslateX(0);
            setDuration(0);
            setOpacity(1);
            phaseRef.current = "idle";

            startCycle();

            return () => clearTimeout(timeoutRef.current);
        }, [text]);

        const handleTransitionEnd = (e) => {
            if (e.propertyName !== "transform") return;
            if (phaseRef.current !== "sliding") return;

            phaseRef.current = "pausing";

            setTimeout(() => {
                // fade out
                setOpacity(0);

                setTimeout(() => {
                    // reset instantly
                    setDuration(0);
                    setTranslateX(0);

                    requestAnimationFrame(() => {
                        const container = containerRef.current;
                        const textEl = textRef.current;

                        if (!container || !textEl) return;

                        const overflow =
                            textEl.scrollWidth - container.offsetWidth;

                        if (overflow > 0) {
                            setOpacity(1);
                            phaseRef.current = "idle";

                            // ⏳ WAIT 2s BEFORE NEXT CYCLE
                            startCycle();
                        }
                    });
                }, FADE);
            }, PAUSE);
        };

        return (
            <div
                ref={containerRef}
                className="overflow-hidden whitespace-nowrap text-[clamp(12px,0.9375vw,18px)]"
            >
                <div
                    ref={textRef}
                    style={{
                        transform: `translateX(${translateX}px)`,
                        transition: `transform ${duration}s linear, opacity ${FADE}ms ease`,
                        opacity: opacity,
                    }}
                    onTransitionEnd={handleTransitionEnd}
                >
                    {text}
                </div>
            </div>
        );
    };


    return (
        <div className="flex-grow ml-2 md:ml-3 pl-2 md:pl-3 w-0 overflow-hidden">
            <div className="flex h-full w-full flex-col justify-center min-w-0 py-0.5">
                <div className={clsx(
                        "truncate text-xs md:text-sm lg:text-base xl:text-lg whitespace-nowrap",
                        isHold ? "text-[#8A82C6]" : "text-gray-400"
                    )}>
                    {isHold ? (
                        <span>
                            {userText || lastUserText}
                            {pulseDots}
                        </span>
                    ) : (
                        userText || lastUserText
                    )}
                </div>{showAssistantRow && (
                <div className="absolute right-0 top-full mt-4 z-50 max-w-[60vw] text-right">

                    <div className="relative inline-block">
                        <div className="relative
                                   max-w-[200px] sm:max-w-[320px] md:max-w-[400px] lg:max-w-[500px] xl:max-w-[550px] 2xl:max-w-[650px]
                                   text-xs md:text-sm lg:text-base xl:text-lg
                                   text-[#838383] leading-tight bg-[#E6E3FF]
                                   rounded-[8px] px-4 py-2
                                   whitespace-nowrap">
                            <span className="block truncate overflow-hidden">
                                {!showAssistantPulse && (
                                    <MarqueeText text={displayReplyText || ""} />
                                )}
                                {showAssistantPulse && (
                                    <span className="inline-block ml-1">
                                        {pulseDots}
                                    </span>
                                )}
                            </span>

                        </div>

                        <span
                            className="absolute -top-[10px] right-[20px] sm:top-[-10px]  md:right-[30px] md:top-[-15px] lg:top[-18px] lg:right-[40px]  z-[-1]"
                            style={{
                                width: "17px",
                                height: "36px",
                                background: "#E6E3FF",
                                clipPath: "polygon(50% 0%, 100% 100%, 0% 100%)"
                            }}
                        />

                    </div>
                </div>
            )}
            </div>
        </div>
    );
};

Conversation.propTypes = {
    conversation: PropTypes.string,
    isHold: PropTypes.bool,
    assistantReply: PropTypes.string,
    isLiveConversation: PropTypes.bool,
    isAwaitingAssistantReply: PropTypes.bool
};


export default Conversation;
